<template>
    <DAlert
        v-if="errors.length && !hideErrors"
        type="alert"
        dismissable
        class="mx-4"
        :title="$t('next_gen.widgets.shared.dependency_error_alert.title')"
        @dismiss="hideErrors = true"
    >
        <DButton
            class="mt-4"
            theme="text-link"
            link-color="alert"
            :link-underline="false"
            @click="retry"
        >
            {{ authErrors.length ? $t('next_gen.widgets.shared.dependency_error_alert.login_retry') : $t('next_gen.widgets.shared.dependency_error_alert.retry') }}
        </DButton>
    </DAlert>
</template>

<script setup>
    import { useIonRouter } from '@ionic/vue'
    import { filter, transform } from 'lodash'
    import { DAlert, DButton } from '@digistorm/spark'

    import dependencyStatus from '@/constants/dependencyStatus'
    import { useWidgetsStore } from '@/stores/widgets'
    import { useAuthHelpers } from '@/composables'

    const emit = defineEmits(['retry'])

    const props = defineProps({
        widgetType: String,
        dependencies: Array,
    })

    const router = useIonRouter()

    const widgetsStore = useWidgetsStore()
    const widgetHelpers = useWidgetHelpers()
    const authHelpers = useAuthHelpers()

    const hideErrors = ref(false)

    const dependencyConfigs = computed(() => {
        return widgetsStore.dependencies(props.widgetType)
    })

    const errors = computed(() => {
        return transform(props.dependencies, (errors, dependency) => {
            const config = dependencyConfigs.value[dependency.url]
            if (!config || !config.error) {
                return
            }
            errors.push(config.error)
        }, [])
    })

    const authErrors = computed(() => {
        return transform(dependencyConfigs.value, (errors, config) => {
            if (config.status !== dependencyStatus.rejected || !authHelpers.isAuthenticationException(config.error)) {
                return
            }
            errors.push(config.error)
        }, [])
    })

    const retry = (skipAuthErrorHandling = false) => {
        if (!skipAuthErrorHandling && authErrors.value.length) {
            authHelpers.handleAuthenticationException(
                authErrors.value[0]?.response?.data,
                router,
                () => {
                    router.back()
                    retry(true)
                },
                () => {
                    router.back()
                    retry(true)
                },
                true,
                false,
            )
            return
        }

        // Retry only the dependencies that failed
        widgetHelpers.fetchDependenciesHandlingRestartIfRequired(props.widgetType, filter(props.dependencies, (dependency) => {
            const config = dependencyConfigs.value[dependency.url]
            return !config || config.error
        }))

        emit('retry')
    }
</script>
